import { useMemo } from "react";
import { ChevronRight } from "@repo/ui";
import { cn } from "@repo/utils";
import type { Status, StatusLog, Workflow } from "~/types/api";
import { LOG_TABLE_COLUMNS, LOG_TABLE_CELL_GAP, formatLogsDateTime, logsCellClass } from "./item-log.constants";

export const LogTableColGroup = () => (
  <colgroup>
    {LOG_TABLE_COLUMNS.map((col) => (
      <col key={col.key} style={{ width: col.width }} />
    ))}
  </colgroup>
);

export const LogTableHeader = () => (
  <thead>
    <tr>
      {LOG_TABLE_COLUMNS.map((col) => (
        <th
          key={col.key}
          className={cn(
            "sticky top-0 z-10 truncate bg-white py-2 text-left text-sm font-semibold text-gray-700 shadow-[inset_0_-1px_0_#e5e7eb]",
            LOG_TABLE_CELL_GAP
          )}
        >
          {col.label}
        </th>
      ))}
    </tr>
  </thead>
);

export const ItemLogsTable = ({
  logs,
  workflows,
  statuses,
}: {
  logs: StatusLog[];
  workflows?: Workflow[];
  statuses?: Status[];
}) => {
  const namesByWorkflowId = useMemo(() => {
    if (!workflows) return {} as Record<number, string>;

    return Object.fromEntries(
      workflows.map((w) => [w.id, `${w.name} v. ${String(w.version).padStart(2, "0")}`])
    ) as Record<number, string>;
  }, [workflows]);

  const withCode = (statusName: string) => {
    const code = statuses?.find((s) => s.name === statusName)?.code;
    return code ? `${code} - ${statusName}` : statusName;
  };

  if (logs.length === 0) {
    return (
      <tr>
        <td colSpan={LOG_TABLE_COLUMNS.length} className="py-8 text-center text-sm text-gray-500">
          No status logs found
        </td>
      </tr>
    );
  }

  return (
    <>
      {logs.map((log) => (
        <tr key={log.id} className="border-b border-gray-100">
          <td className={logsCellClass}>{formatLogsDateTime(log.createdUtc)}</td>
          <td className={logsCellClass} title={log.username}>{log.username}</td>
          <td className={logsCellClass} title={withCode(log.fromStatus)}>{withCode(log.fromStatus)}</td>
          <td className={cn("text-gxe-primary py-3 text-sm font-bold", LOG_TABLE_CELL_GAP)}>
            <ChevronRight className="inline-block size-4" />
          </td>
          <td className={logsCellClass} title={withCode(log.toStatus)}>{withCode(log.toStatus)}</td>
          <td className={logsCellClass}>
            {log.workflowId ? namesByWorkflowId[log.workflowId] : "—"}
          </td>
        </tr>
      ))}
    </>
  );
};
